'use client';

import * as React from 'react';
import {
  CalendarCheck,
  CarFront,
  CheckCircle,
  Phone,
  ShieldCheck,
  Star
} from 'lucide-react';
import { motion } from 'motion/react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@workspace/ui/components/tabs';

import { BlurFade } from '~/components/fragments/blur-fade';
import { GridSection } from '~/components/fragments/grid-section';
import { TextGenerateEffect } from '~/components/fragments/text-generate-effect';

const rentalSteps = [
  {
    number: 1,
    title: 'Alegeți mașina',
    description:
      'Selectați mașina perfectă pentru nevoile dumneavoastră din flota noastră variată de vehicule premium.',
    icon: CarFront
  },
  {
    number: 2,
    title: 'Rezervați datele',
    description: 'Alegeți datele de preluare și predare convenabile.',
    icon: CalendarCheck
  },
  {
    number: 3,
    title: 'Bucurați-vă de călătorie',
    description:
      'Preluați mașina și bucurați-vă de experiența fără griji și complicații.',
    icon: Star
  }
];

const rcaSteps = [
  {
    number: 1,
    title: 'Ați avut un accident?',
    description:
      'Dacă ați fost victima unui accident auto și nu sunteți vinovați, aveți dreptul legal la o mașină de schimb decontată prin RCA.',
    icon: ShieldCheck
  },
  {
    number: 2,
    title: 'Contactați-ne imediat',
    description:
      'Sunați-ne sau completați formularul nostru. Vom gestiona toate formalitățile cu asigurarea pentru dumneavoastră.',
    icon: Phone
  },
  {
    number: 3,
    title: 'Primiți mașina de lux',
    description:
      'Livrăm mașina premium decontată prin RCA la adresa dorită și vă explicăm toate detaliile despre perioada de folosire.',
    icon: CheckCircle
  }
];

function StepsGrid({
  steps
}: {
  steps: typeof rentalSteps;
}): React.JSX.Element {
  return (
    <div className="grid gap-16 sm:grid-cols-1 md:grid-cols-3">
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <BlurFade
            key={step.number}
            inView
            delay={0.1 + index * 0.1}
          >
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary/10 text-primary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <Icon className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {step.number}. {step.title}
              </h3>
              <p className="text-muted-foreground">{step.description}</p>
            </div>
          </BlurFade>
        );
      })}
    </div>
  );
}

export function HowItWorks(): React.JSX.Element {
  return (
    <GridSection>
      <div className="bg-background container flex flex-col px-4 py-20 md:px-6">
        <div className="mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-semibold tracking-tight md:text-5xl">
              <TextGenerateEffect words="Mobilitate premium, explicată simplu" />
            </h2>
            <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-base">
              Indiferent de situație, avem soluția potrivită pentru mobilitatea
              dumneavoastră
            </p>
          </motion.div>
        </div>

        <Tabs
          defaultValue="rental"
          className="w-full"
        >
          <TabsList className="mx-auto mb-12 grid w-fit grid-cols-2">
            <TabsTrigger
              value="rental"
              className="text-sm"
            >
              Închiriere de lux
            </TabsTrigger>
            <TabsTrigger
              value="rca"
              className="text-sm"
            >
              Autoturism asigurat prin RCA
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="rental"
            className="mt-0"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-xl font-semibold">Închiriere de lux</h3>
              <p className="text-muted-foreground">
                Închirierea unei mașini de lux nu a fost niciodată mai ușoară
              </p>
            </div>
            <StepsGrid steps={rentalSteps} />
          </TabsContent>

          <TabsContent
            value="rca"
            className="mt-0"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-xl font-semibold">
                Autoturism asigurat prin RCA
              </h3>
              <p className="text-muted-foreground">
                Dreptul dumneavoastră legal la o mașină de înlocuire premium
              </p>
            </div>
            <StepsGrid steps={rcaSteps} />
          </TabsContent>
        </Tabs>
      </div>
    </GridSection>
  );
}
